/**
 * ============================================================================
 * ACTIVITY ANALYTICS SERVICE - Phase 2
 * ============================================================================
 * Provides comprehensive analytics and insights for activity tracking
 *
 * @pattern Phase 4 - IService + ServiceResponse + typedSupabase
 * @version 2.0.0
 * @date 2026-02-20
 */
import { typedSupabase } from "../lib/typed-supabase";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError} from "./service-registry";
import { logger } from "../lib/logger";
// ============================================================================
// TYPES
// ============================================================================
export interface ActivityTrend {
    period: string;
    domain: string;
    action_type: string;
    count: number;
}
export interface UserProductivityMetrics {
    total_activities: number;
    active_days: number;
    avg_activities_per_day: number;
    most_common_action: string;
    most_common_action_count: number;
    activity_by_domain: Record<string, number>;
}
export interface TeamMember {
    user_id: string;
    username: string;
    full_name: string;
    avatar_url: string;
    total_activities: number;
    active_days: number;
    rank: number;
}
export interface HeatmapData {
    day_of_week: number;
    hour_of_day: number;
    activity_count: number;
}
export interface ActivityDistribution {
    activity_domain: string;
    action_type: string;
    count: number;
    percentage: number;
}
export interface GetActivityTrendsParams {
    startDate: string;
    endDate: string;
    domain?: 'task' | 'sales' | 'organization';
    interval?: 'hour' | 'day' | 'week' | 'month';
}
export interface GetUserProductivityParams {
    userId: string;
    startDate: string;
    endDate: string;
}
export interface GetTeamLeaderboardParams {
    organizationId: string;
    startDate: string;
    endDate: string;
    limit?: number;
}
export interface GetHeatmapParams {
    userId?: string;
    startDate?: string;
    endDate?: string;
}
// ============================================================================
// SERVICE INTERFACE
// ============================================================================
export interface IActivityAnalyticsService extends IService {
    /**
     * Get activity trends over time
     * @param params - Date range, domain, and interval
     * @returns Time-series data of activity counts
     */
    getActivityTrends(params: GetActivityTrendsParams): Promise<ServiceResponse<ActivityTrend[]>>;
    /**
     * Get productivity metrics for a specific user
     * @param params - User ID and date range
     * @returns Comprehensive productivity statistics
     */
    getUserProductivityMetrics(params: GetUserProductivityParams): Promise<ServiceResponse<UserProductivityMetrics>>;
    /**
     * Get team activity leaderboard
     * @param params - Organization ID, date range, and limit
     * @returns Ranked list of team members by activity
     */
    getTeamActivityLeaderboard(params: GetTeamLeaderboardParams): Promise<ServiceResponse<TeamMember[]>>;
    /**
     * Get activity heat map data (hour x day of week)
     * @param params - Optional user ID and date range
     * @returns Heat map data for visualization
     */
    getActivityHeatmap(params: GetHeatmapParams): Promise<ServiceResponse<HeatmapData[]>>;
    /**
     * Get activity distribution by type and domain
     * @returns Distribution statistics
     */
    getActivityDistribution(): Promise<ServiceResponse<ActivityDistribution[]>>;
}
// ============================================================================
// SERVICE IMPLEMENTATION
// ============================================================================
export class ActivityAnalyticsService implements IActivityAnalyticsService {
    readonly name = "ActivityAnalyticsService";
    async initialize(): Promise<void> {
        logger.info("✅ ActivityAnalyticsService initialized");
    }
    async dispose(): Promise<void> {
        logger.info("🧹 ActivityAnalyticsService disposed");
    }
    async getActivityTrends(params: GetActivityTrendsParams): Promise<ServiceResponse<ActivityTrend[]>> {
        const { startDate, endDate, domain, interval = 'day' } = params;
        logger.debug("Fetching activity trends", { startDate, endDate, domain, interval });
        try {
            const { data, error } = await typedSupabase.rpc("get_activity_trends", {
                p_start_date: startDate,
                p_end_date: endDate,
                p_domain: domain || null,
                p_interval: interval,
            });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            return { success: true, data: (data as ActivityTrend[]) || [] };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    async getUserProductivityMetrics(params: GetUserProductivityParams): Promise<ServiceResponse<UserProductivityMetrics>> {
        const { userId, startDate, endDate } = params;
        logger.debug("Fetching user productivity metrics", { userId, startDate, endDate });
        try {
            const { data, error } = await typedSupabase.rpc("get_user_productivity_metrics", {
                p_user_id: userId,
                p_start_date: startDate,
                p_end_date: endDate,
            });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            if (!data || data.length === 0) {
                return { success: true, data: {
                        total_activities: 0,
                        active_days: 0,
                        avg_activities_per_day: 0,
                        most_common_action: '',
                        most_common_action_count: 0,
                        activity_by_domain: {},
                    } };
            }
            const metrics = data[0] as any;
            return { success: true, data: {
                    total_activities: Number(metrics.total_activities) || 0,
                    active_days: Number(metrics.active_days) || 0,
                    avg_activities_per_day: Number(metrics.avg_activities_per_day) || 0,
                    most_common_action: metrics.most_common_action || '',
                    most_common_action_count: Number(metrics.most_common_action_count) || 0,
                    activity_by_domain: metrics.activity_by_domain || {},
                } };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    async getTeamActivityLeaderboard(params: GetTeamLeaderboardParams): Promise<ServiceResponse<TeamMember[]>> {
        const { organizationId, startDate, endDate, limit = 10 } = params;
        logger.debug("Fetching team activity leaderboard", { organizationId, startDate, endDate, limit });
        try {
            const { data, error } = await typedSupabase.rpc("get_team_activity_leaderboard", {
                p_organization_id: organizationId,
                p_start_date: startDate,
                p_end_date: endDate,
                p_limit: limit,
            });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            const members = (data as any[])?.map(member => ({
                user_id: member.user_id,
                username: member.username || '',
                full_name: member.full_name || '',
                avatar_url: member.avatar_url || '',
                total_activities: Number(member.total_activities) || 0,
                active_days: Number(member.active_days) || 0,
                rank: Number(member.rank) || 0,
            })) || [];
            return { success: true, data: members };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    async getActivityHeatmap(params: GetHeatmapParams): Promise<ServiceResponse<HeatmapData[]>> {
        const { userId, startDate, endDate } = params;
        logger.debug("Fetching activity heatmap", { userId, startDate, endDate });
        try {
            const { data, error } = await typedSupabase.rpc("get_activity_heatmap", {
                p_user_id: userId || null,
                p_start_date: startDate || null,
                p_end_date: endDate || null,
            });
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            const heatmapData = (data as any[])?.map(item => ({
                day_of_week: Number(item.day_of_week),
                hour_of_day: Number(item.hour_of_day),
                activity_count: Number(item.activity_count),
            })) || [];
            return { success: true, data: heatmapData };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    async getActivityDistribution(): Promise<ServiceResponse<ActivityDistribution[]>> {
        logger.debug("Fetching activity distribution");
        try {
            const { data, error } = await typedSupabase
                .from("v_activity_distribution" as any)
                .select("*");
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            const distribution = (data as any[])?.map(item => ({
                activity_domain: item.activity_domain,
                action_type: item.action_type,
                count: Number(item.count) || 0,
                percentage: Number(item.percentage) || 0,
            })) || [];
            return { success: true, data: distribution };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
}
// ============================================================================
// EXPORT SINGLETON
// ============================================================================
export const activityAnalyticsService = new ActivityAnalyticsService();
